import { useState, useMemo } from "react";
import { PageHeader } from "@/components/PageHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Copy, Download, Globe, Code2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { company } from "@/lib/branding";

export default function EmbedGenerator() {
    const backendOrigin = process.env.REACT_APP_BACKEND_URL || "";
    const [cfg, setCfg] = useState({
        company: company(),
        primary: "#0066ff",
        accent: "#ff6600",
        defaultZip: "06475",
        successMsg: "Thank you — your service is being scheduled.",
    });
    const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));

    const signupUrl = `${backendOrigin}/signup?embed=1&color=${encodeURIComponent(cfg.primary)}&zip=${cfg.defaultZip}`;

    const jsSnippet = useMemo(() => `<!-- ${cfg.company} signup widget -->
<div id="rd-signup-widget" data-zip="${cfg.defaultZip}"></div>
<script>
(function(){
  var BASE = "${backendOrigin}";
  var d = document.getElementById('rd-signup-widget');
  if (!d) return;
  var f = document.createElement('iframe');
  f.src = BASE + "/signup?embed=1&color=${encodeURIComponent(cfg.primary)}&zip=" + (d.getAttribute('data-zip') || "${cfg.defaultZip}");
  f.style.width = "100%";
  f.style.minHeight = "780px";
  f.style.border = "0";
  f.style.borderRadius = "12px";
  f.allow = "payment";
  f.setAttribute('title', '${cfg.company} signup');
  d.appendChild(f);
  window.addEventListener('message', function(e){
    if (e.origin === BASE && e.data && e.data.rdHeight) f.style.height = e.data.rdHeight + 'px';
  });
})();
</script>
`, [cfg, backendOrigin]);

    const phpPlugin = useMemo(() => `<?php
/**
 * Plugin Name: ${cfg.company} Signup
 * Description: Embeds the ${cfg.company} customer signup form via shortcode [routehq_signup].
 * Version: 1.0.0
 * Author: ${cfg.company}
 */

if (!defined('ABSPATH')) exit;

function routehq_signup_shortcode($atts) {
    $atts = shortcode_atts(array(
        'zip' => '${cfg.defaultZip}',
        'height' => '780',
    ), $atts);

    $src = '${signupUrl}';
    $zip = esc_attr($atts['zip']);
    $h   = esc_attr($atts['height']);

    return '<div class="rd-signup-widget"><iframe src="' . esc_url($src) . '&zip=' . $zip . '" style="width:100%;min-height:' . $h . 'px;border:0;border-radius:12px" allow="payment" title="${cfg.company} signup"></iframe></div>';
}
add_shortcode('routehq_signup', 'routehq_signup_shortcode');
`, [cfg, signupUrl]);

    const copy = async (text, label) => {
        try {
            await navigator.clipboard.writeText(text);
            toast.success(`${label} copied to clipboard`);
        } catch {
            toast.error("Copy failed — select and copy manually");
        }
    };

    const download = (filename, content, mime = "text/plain") => {
        const blob = new Blob([content], { type: mime });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = filename;
        a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <div data-testid="embed-page">
            <PageHeader
                title="Website Embed"
                subtitle="Drop the signup wizard onto any site. WordPress shortcode or a one-line JavaScript snippet."
            />
            <div className="px-8 py-6 grid lg:grid-cols-3 gap-4">
                {/* Config */}
                <Card>
                    <CardContent className="p-6 space-y-3">
                        <div className="overline">Configuration</div>
                        <h3 className="font-heading text-lg font-semibold mb-2">Customize the widget</h3>
                        <div className="space-y-3">
                            <Fld label="Company name"><Input value={cfg.company} onChange={(e) => set("company", e.target.value)} data-testid="embed-company" /></Fld>
                            <Fld label="Primary color"><Input type="color" value={cfg.primary} onChange={(e) => set("primary", e.target.value)} data-testid="embed-primary" /></Fld>
                            <Fld label="Default ZIP"><Input value={cfg.defaultZip} onChange={(e) => set("defaultZip", e.target.value)} data-testid="embed-zip" /></Fld>
                            <Fld label="Success message"><Input value={cfg.successMsg} onChange={(e) => set("successMsg", e.target.value)} data-testid="embed-success" /></Fld>
                        </div>
                    </CardContent>
                </Card>

                {/* Snippet / Plugin */}
                <Card className="lg:col-span-2">
                    <CardContent className="p-6">
                        <Tabs defaultValue="js">
                            <TabsList>
                                <TabsTrigger value="js" data-testid="tab-js"><Code2 className="w-4 h-4 mr-1" />JavaScript snippet</TabsTrigger>
                                <TabsTrigger value="wp" data-testid="tab-wp"><Globe className="w-4 h-4 mr-1" />WordPress plugin</TabsTrigger>
                                <TabsTrigger value="preview" data-testid="tab-preview">Preview</TabsTrigger>
                            </TabsList>

                            <TabsContent value="js" className="space-y-3 mt-4">
                                <div className="overline">Universal embed</div>
                                <p className="text-sm text-muted-foreground">Drop this on any HTML page — Wix, Webflow, Squarespace, plain WordPress block, etc.</p>
                                <pre className="font-mono text-[11px] p-4 rounded-md border border-border bg-secondary/40 overflow-x-auto max-h-96"
                                     data-testid="embed-js-snippet">{jsSnippet}</pre>
                                <div className="flex gap-2">
                                    <Button onClick={() => copy(jsSnippet, "Snippet")} data-testid="embed-js-copy">
                                        <Copy className="w-4 h-4 mr-1" />Copy snippet
                                    </Button>
                                    <Button variant="outline" onClick={() => download("routehq-signup.html", jsSnippet, "text/html")} data-testid="embed-js-download">
                                        <Download className="w-4 h-4 mr-1" />Download .html
                                    </Button>
                                </div>
                            </TabsContent>

                            <TabsContent value="wp" className="space-y-3 mt-4">
                                <div className="overline">WordPress plugin</div>
                                <p className="text-sm text-muted-foreground">
                                    Save as <span className="font-mono text-xs">routehq-signup.php</span> and upload to <span className="font-mono text-xs">/wp-content/plugins/</span>, then activate.
                                    Use the shortcode <span className="font-mono text-xs">[routehq_signup]</span> on any page.
                                </p>
                                <pre className="font-mono text-[11px] p-4 rounded-md border border-border bg-secondary/40 overflow-x-auto max-h-96"
                                     data-testid="embed-php-snippet">{phpPlugin}</pre>
                                <div className="flex gap-2">
                                    <Button onClick={() => copy(phpPlugin, "Plugin code")} data-testid="embed-php-copy">
                                        <Copy className="w-4 h-4 mr-1" />Copy PHP
                                    </Button>
                                    <Button variant="outline" onClick={() => download("routehq-signup.php", phpPlugin, "application/x-php")}
                                            data-testid="embed-php-download">
                                        <Download className="w-4 h-4 mr-1" />Download plugin
                                    </Button>
                                </div>
                                <div className="flex items-start gap-2 p-3 rounded-md bg-primary/5 border border-primary/20 text-xs">
                                    <CheckCircle2 className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                                    <div>
                                        <div className="font-semibold mb-0.5">Shortcode options</div>
                                        <code className="block opacity-80">[routehq_signup zip="06475" height="780"]</code>
                                    </div>
                                </div>
                            </TabsContent>

                            <TabsContent value="preview" className="mt-4">
                                <div className="overline mb-2">Live preview</div>
                                <iframe src={signupUrl} title="Signup preview" data-testid="embed-preview"
                                        className="w-full h-[700px] rounded-md border border-border" />
                            </TabsContent>
                        </Tabs>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}

function Fld({ label, children }) {
    return <div className="space-y-1.5"><Label className="text-xs">{label}</Label>{children}</div>;
}
